<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { fetchPageBuilder, invokeKey } from '@drapejs/core';
import { useCartFunctions } from '@/helpers/cart-actions';
import Checkbox from './forms/Checkbox.vue';
import CartItem from './CartItem.vue';
import useContext from '@/composables/useContext';
import CartPriceItem from './CartPriceItem.vue';
import ConfiguratorChevron from './ProductConfigurator/ConfiguratorChevron.vue';
import * as vingaSales from '../../connectors/litium/sales.ts';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import { getAddonTitle } from '@/helpers/printess';

const props = defineProps({
  group: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['edit']);
const invoke = inject(invokeKey, async () => null);
const { websiteText } = useWebsiteTexts();
const { formatPrice, route, isAuthorized, navigateToLoginPage } = useContext();
const { removeGroupFromCart } = useCartFunctions();
const isPriceExpanded = ref(false);

const productName = computed(() => props.group.baseProductName || '');
const hasPrintRow = computed(() =>
  props.group.rows.some((r) => r.printRow?.unitPrice)
);
const hasGiftCardAddons = computed(() =>
  props.group.rows.some(
    (r) => r.giftCardInfo && r.giftCardInfo?.addons?.length > 0
  )
);
const hasGiftCardShipping = computed(() =>
  props.group.rows.some(
    (r) => r.giftCardInfo && r.giftCardInfo?.giftCardShipping !== null
  )
);
const upsellItems = computed(() => {
  return props.group.rows[0].upsellItems;
});

const mappedProducts = computed(() => {
  return (
    props.group.rows.map((row) => {
      const name = getRowName(row);
      const originalPrice = getOriginalPrice(row);
      const currentPrice = getCurrentPrice(row);
      const campaignPercentage = getCampaignPercentage(row);
      return {
        name,
        id: row.id,
        quantity: row.quantity,
        currentPrice,
        originalPrice,
        campaignPercentage,
        addons: row.addOns,
        articleNumber: row.articleNumber,
        articleNumberForDisplay: row.articleNumberForDisplay
      };
    }) || []
  );
});

const mappedDecorations = computed(() => {
  if (!hasPrintRow.value) return [];
  const print = props.group.rows[0].printRow;
  const addons = props.group.rows[0].addOns.filter((a) => a.unitPrice);
  const quantity =
    mappedProducts.value.reduce((acc, curr) => acc + curr.quantity, 0) || 1;

  return [
    {
      unitPrice: print.unitPrice,
      quantity,
      name: print.name || '',
      addons,
      id: print.systemId
    }
  ];
});

const decorationPriceSum = computed(() => {
  if (!hasPrintRow.value) return 0;
  const decoration = mappedDecorations.value[0];
  const decorationUnitsTotal = decoration.unitPrice * decoration.quantity;
  return (
    decorationUnitsTotal +
    decoration.addons.reduce((acc, a) => acc + a.unitPrice, 0)
  );
});

const showEditButton = computed(() => {
  return props.group.rows.every((r) => r.giftCardInfo === null);
});

const mappedGiftCardAddons = computed(() => {
  if (!hasGiftCardAddons.value) return [];
  const addons = props.group.rows[0].giftCardInfo?.addons;
  return addons.map((a) => {
    return {
      unitPrice: a.price,
      quantity: a.quantity,
      name: getAddonTitle(a) || '',
      id: a.id
    };
  });
});

const giftcardShippingPriceSum = computed(() => {
  if (!hasGiftCardShipping.value) return 0;
  return mappedGiftCardShipping.value.reduce(
    (acc, a) => acc + a.unitPrice * a.quantity,
    0
  );
});

const mappedGiftCardShipping = computed(() => {
  if (!hasGiftCardShipping.value) return [];
  const shipping = props.group.rows[0].giftCardInfo?.giftCardShipping;
  return [
    {
      unitPrice: shipping.price,
      quantity: shipping.quantity,
      name: websiteText('giftcard__shipping').value,
      id: shipping.id || 'PKFRAKT'
    }
  ];
});

const giftcardAddonsPriceSum = computed(() => {
  if (!hasGiftCardAddons.value) return 0;
  return mappedGiftCardAddons.value.reduce(
    (acc, a) => acc + a.unitPrice * a.quantity,
    0
  );
});

const productPriceSum = computed(() => {
  return mappedProducts.value.reduce((acc, r) => {
    const rowSum = r.currentPrice * r.quantity;
    return acc + rowSum;
  }, 0);
});

const groupPriceSum = computed(() => {
  const activeUpsell = upsellItems.value.reduce((acc, u) => {
    return u.isActive ? acc + u.value : acc;
  }, 0);
  return (
    decorationPriceSum.value +
    giftcardAddonsPriceSum.value +
    productPriceSum.value +
    giftcardShippingPriceSum.value +
    activeUpsell
  );
});

function getRowName(row) {
  let variant = '';
  if (row.color && row.size) {
    variant += row.size ? `${row.color}, ${row.size}` : row.color;
  } else if (row.size) {
    variant += row.size || '';
  } else {
    variant = row.variantName;
  }
  return variant;
}

function getCurrentPrice(row) {
  return row.unitPrice ?? row.unitListPrice;
}

function getOriginalPrice(row) {
  return row.originalPrice >= getCurrentPrice(row)
    ? row.originalPrice
    : getCurrentPrice(row);
}

function getCampaignPercentage(row) {
  const current = getCurrentPrice(row);
  const original = getOriginalPrice(row);

  if (current < original) {
    const percentage = Math.abs(((original - current) / original) * -100);
    if (percentage > 1) {
      return Number(formatPrice(percentage, undefined, '', '', 'decimal'));
    }
  }

  return 0;
}

function getUpsellItemText(upsellItem) {
  const text =
    upsellItem.text?.charAt(0).toLowerCase() + upsellItem.text?.slice(1);
  const variantName = `${text} (${formatPrice(upsellItem.value)})`;

  return websiteText('global__add_upsell', { variant: variantName }).value;
}

function getUpsellItemPrice(upsellItem) {
  if (upsellItem.isActive) {
    return formatPrice(upsellItem.value);
  }
  return '';
}

async function removeProduct() {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  const { _, error } = await removeGroupFromCart(
    props.group.groupId,
    props.group.rowId
  );
}

function editIfAuthorized(group) {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  emit('edit', {
    id: group.groupId,
    url: group.baseProductUrl,
    name: productName,
    articleNumber: group.articleNumber
  });
}

function buildRequest() {
  return fetchPageBuilder(
    route.protocol,
    route.host,
    route.pathname,
    {
      ...route.query
    },
    ''
  );
}

async function toggleUpsellItem(upsellItem) {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  const request = buildRequest();
  request.rowSystemId = props.group.rowId;
  request.variantSystemId = props.group.variantSystemId;
  request.upsellItemRowSystemId = upsellItem.rowSystemId;
  request.upsellItemVariantSystemId = upsellItem.variantSystemId;
  request.isUpsellItem = true;
  request.quantity = upsellItem.isActive ? 1 : 0;

  await invoke(vingaSales.commands.setCartRowUpsellProduct, request);
}
</script>

<template>
  <div class="cart-group-item">
    <div class="cart-group-item__toolbar">
      <div>
        <div class="cart-group-item__title">
          {{ productName }}
        </div>
        <div v-if="hasPrintRow" class="cart-group-item__decorated">
          {{ websiteText('decoration__decorated').value }}
        </div>
      </div>
      <button
        v-if="showEditButton"
        class="cart-group-item__edit secondary-button"
        @click="editIfAuthorized(group)"
      >
        {{ websiteText('global__edit').value }}
      </button>
    </div>
    <div class="cart-group-item__rows">
      <CartItem
        v-for="row in group.rows"
        :campaign-percentage="getCampaignPercentage(row)"
        :key="row.id"
        :row="row"
      />
    </div>
    <div class="cart-group-item__remove-group">
      <button
        class="cart-group-item__remove-group-button"
        @click="removeProduct()"
      >
        {{ websiteText('global__remove').value }}
        <img src="../static/icons/trash_icon.svg" alt="trash icon" />
      </button>
    </div>
    <div class="cart-group-item__pricing">
      <div
        class="cart-group-item__pricing-summary"
        @click="isPriceExpanded = !isPriceExpanded"
      >
        <div class="cart-group-item__subtotal">
          <div>
            {{ websiteText('global__subtotal').value }}
          </div>
          <div class="cart-group-item__subtotal-price">
            <div>{{ formatPrice(groupPriceSum) }}</div>
            <ConfiguratorChevron
              class="cart-group-item__subtotal-chevron"
              :open="isPriceExpanded"
            ></ConfiguratorChevron>
          </div>
        </div>
      </div>
      <div v-if="isPriceExpanded" class="cart-group-item__pricing-content">
        <div class="cart-group-item__price-items">
          <div class="cart-group-item__price-items-title">
            <div>
              {{ websiteText('global__label_products_menu').value }}
            </div>
            <div>
              {{ formatPrice(productPriceSum) }}
            </div>
          </div>
          <CartPriceItem
            v-for="row in mappedProducts"
            :key="row.id"
            :item="row"
            :is-decoration="false"
          />
        </div>
        <div v-if="hasGiftCardAddons" class="cart-group-item__price-items">
          <div class="cart-group-item__price-items-title">
            <div>
              {{ websiteText('decoration__decorations').value }}
            </div>
            <div>
              {{ formatPrice(giftcardAddonsPriceSum) }}
            </div>
          </div>
          <CartPriceItem
            v-for="row in mappedGiftCardAddons"
            :key="row.id"
            :item="row"
            :is-decoration="true"
          />
        </div>
        <div v-if="hasPrintRow" class="cart-group-item__price-items">
          <div class="cart-group-item__price-items-title">
            <div>
              {{ websiteText('decoration__decorations').value }}
            </div>
            <div>
              {{ formatPrice(decorationPriceSum) }}
            </div>
          </div>
          <CartPriceItem
            v-for="row in mappedDecorations"
            :key="row.id"
            :item="row"
            :is-decoration="true"
          />
        </div>
        <div class="cart-group-item__upsell-items">
          <div
            v-for="upsellItem in upsellItems"
            :key="upsellItem.text"
            class="cart-group-item__upsell-item"
          >
            <checkbox
              :text="getUpsellItemText(upsellItem)"
              v-model="upsellItem.isActive"
              @change="toggleUpsellItem(upsellItem)"
            />
            <span>{{ getUpsellItemPrice(upsellItem) }}</span>
          </div>
        </div>
        <div v-if="hasGiftCardShipping" class="cart-group-item__price-items">
          <div class="cart-group-item__price-items-title">
            <div>
              {{ websiteText('checkout__freight').value }}
            </div>
            <div>
              {{ formatPrice(giftcardShippingPriceSum) }}
            </div>
          </div>
          <CartPriceItem
            v-for="row in mappedGiftCardShipping"
            :key="row.id"
            :item="row"
            :is-decoration="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.cart-group-item {
  padding: 10px 12px 6.5px;
  box-shadow: 4px 5px 20px rgba(0, 81, 121, 0.07);
  border-radius: 3px;
  background-color: #fff;
  margin-bottom: 18px;
  color: var(--color-text-darker);
}

button.cart-group-item__edit {
  cursor: pointer;
  padding: 11.5px 20px;
  height: auto;
}

.cart-group-item__toolbar {
  display: flex;
  justify-content: space-between;
}

.cart-group-item__title {
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
}

.cart-group-item__decorated {
  font-size: 12px;
  line-height: 16px;
  font-style: italic;
  margin-top: 3px;
}

.cart-group-item__rows {
  margin: 10px 0 8px;
  display: grid;
  gap: 10px;
}

.cart-group-item__remove-group {
  margin-bottom: 10px;
}

button.cart-group-item__remove-group-button {
  margin-left: auto;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 17px;
  color: var(--color-neutrals-80);
  font-weight: 700;
  padding: 0;
}

.cart-group-item__pricing {
  border-top: 1px solid var(--color-neutrals-20);
  margin: 0 -12px;
  padding: 0 12px;
}

.cart-group-item__pricing-summary {
  cursor: pointer;
}

.cart-group-item__subtotal {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 6.5px;
}

.cart-group-item__subtotal-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-group-item__subtotal-chevron {
  margin-left: 9px;
}

.cart-group-item__price-items {
  display: grid;
  gap: 8px;
  margin-bottom: 10px;
  padding-right: 16px;
  padding-bottom: 8px;
}

.cart-group-item__price-items-title {
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-group-item__pricing-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 26px;
  color: #1a1a1a;
}

.cart-group-item__upsell-items {
  font-size: 14px;
  line-height: 24px;
  color: #1a1a1a;
  padding-right: 16px;
}

.cart-group-item__upsell-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 5px;
}

.cart-group-item__pricing-content {
  border-top: 1px solid var(--color-neutrals-20);
  margin: 6.5px -12px 0;
  padding: 9.5px 12px 8px;
}
</style>
